import SeoHead from '../../components/feature/SeoHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Tasación Gratuita de Coches en Barcelona',
    description: 'Servicio de tasación gratuita y sin compromiso para todo tipo de vehículos en Barcelona y provincia. Respuesta en menos de 24 horas.',
    provider: {
      '@type': 'AutoDealer',
      name: 'Desguace Barcelona',
      telephone: '+34932932913',
      address: { '@type': 'PostalAddress', addressLocality: 'Barcelona', addressCountry: 'ES' },
    },
    areaServed: { '@type': 'AdministrativeArea', name: 'Provincia de Barcelona' },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Tasación completamente gratuita y sin compromiso' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿La tasación es realmente gratuita?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, completamente gratuita y sin ningún compromiso. No cobramos nada por evaluar tu vehículo y ofrecerte un precio.' },
      },
      {
        '@type': 'Question',
        name: '¿Cuánto tiempo tarda la tasación?',
        acceptedAnswer: { '@type': 'Answer', text: 'Recibirás una oferta detallada en menos de 24 horas desde que nos proporcionas la información del vehículo.' },
      },
      {
        '@type': 'Question',
        name: '¿Qué información necesitan para tasar mi coche?',
        acceptedAnswer: { '@type': 'Answer', text: 'Necesitamos: matrícula, marca, modelo, año de fabricación, kilometraje, estado general y, si es posible, algunas fotos del vehículo.' },
      },
      {
        '@type': 'Question',
        name: '¿El precio de la tasación es definitivo?',
        acceptedAnswer: { '@type': 'Answer', text: 'La oferta que te hacemos es firme siempre que la información proporcionada sea correcta.' },
      },
    ],
  },
];

export default function TasacionCochePage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Tasación Gratuita de Coche en Barcelona | Respuesta en 24h | Desguace Barcelona"
        description="Tasación gratuita y sin compromiso de tu coche en Barcelona en menos de 24 horas. Coches siniestrados, averiados, sin ITV o en cualquier estado. Mejor precio garantizado."
        keywords="tasación coche Barcelona, valorar coche Barcelona, cuánto vale mi coche, tasación gratuita coche, precio coche siniestrado Barcelona"
        canonical={`${siteUrl}/tasacion-coche-barcelona`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a2332] to-[#2d3e50]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Tasación de Coche en Barcelona
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Tasación gratuita y sin compromiso en menos de 24 horas. Descubre cuánto vale tu coche hoy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20tasar%20mi%20coche%20gratis" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1da851] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-3"></i>
                Tasar mi Coche Gratis
              </a>
              <a 
                href="tel:+34932932913" 
                className="inline-flex items-center justify-center px-10 py-5 bg-white text-[#1a2332] rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-3"></i>
                932 932 913
              </a>
            </div>
          </div>
        </section>

        {/* Señales de Confianza */}
        <section className="py-12 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-money-euro-circle-line text-white text-3xl"></i>
                </div>
                <p className="font-bold text-[#1a2332] text-sm">Mejor Precio</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-white text-3xl"></i>
                </div>
                <p className="font-bold text-[#1a2332] text-sm">Respuesta en 24h</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-file-list-3-line text-white text-3xl"></i>
                </div>
                <p className="font-bold text-[#1a2332] text-sm">Sin Compromiso</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-white text-3xl"></i>
                </div>
                <p className="font-bold text-[#1a2332] text-sm">100% Gratuito</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contenido Principal */}
        <article className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Introducción */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ¿Quieres saber cuánto vale tu coche? En <strong>Desguace Barcelona</strong> ofrecemos un servicio de <strong>tasación gratuita y sin compromiso</strong> para todo tipo de vehículos en Barcelona y provincia. Con más de 15 años de experiencia en el sector, nuestro equipo de expertos evalúa tu coche de forma profesional y te ofrece el mejor precio del mercado.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Ya sea que tu coche esté en perfecto estado, tenga averías, esté siniestrado o simplemente quieras venderlo rápido, nuestra tasación te ayudará a conocer su valor real en el mercado actual. El proceso es rápido, transparente y completamente gratuito.
              </p>
            </div>

            {/* Sección 1 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                ¿Cómo Funciona Nuestra Tasación Gratuita?
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#ff6b35] text-white rounded-full flex items-center justify-center font-black text-lg">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Proporciona los Datos del Vehículo</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Rellena el formulario online o llámanos por teléfono. Necesitamos información básica: matrícula, marca, modelo, año, kilometraje y estado general del vehículo. Si tienes fotos, mejor aún.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#ff6b35] text-white rounded-full flex items-center justify-center font-black text-lg">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Evaluación por Expertos</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Nuestro equipo de profesionales analiza toda la información: estado del vehículo, demanda de piezas, valor de mercado, historial y otros factores relevantes para determinar el precio más justo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#ff6b35] text-white rounded-full flex items-center justify-center font-black text-lg">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Recibe tu Tasación en 24h</h4>
                      <p className="text-gray-600 leading-relaxed">
                        En menos de 24 horas recibirás una oferta detallada y transparente. Te explicamos cómo hemos llegado al precio y qué factores hemos considerado. Sin letra pequeña ni sorpresas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#ff6b35] text-white rounded-full flex items-center justify-center font-black text-lg">
                      4
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Decide Sin Presión</h4>
                      <p className="text-gray-600 leading-relaxed">
                        La tasación es completamente gratuita y sin compromiso. Tómate el tiempo que necesites para decidir. Si aceptas, programamos la recogida y el pago inmediato. Si no, no pasa nada.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Sección 2 */}
            <section className="mb-16 bg-gradient-to-br from-[#f8f9fa] to-[#e8f0f7] p-8 rounded-2xl">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Factores que Determinan el Valor de tu Coche
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Nuestra tasación es exhaustiva y considera múltiples aspectos para ofrecerte el precio más justo:
              </p>

              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-car-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Marca y Modelo</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Algunas marcas tienen mayor demanda en el mercado de recambios. Vehículos premium (BMW, Mercedes, Audi) y modelos populares (Seat, Volkswagen, Renault) suelen tener mejor valoración.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-calendar-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Año de Fabricación</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Los vehículos más recientes tienen piezas en mejor estado y mayor demanda. Sin embargo, también compramos coches antiguos, ya que muchas piezas clásicas tienen valor para coleccionistas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-dashboard-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Kilometraje</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Menor kilometraje generalmente significa piezas menos desgastadas. Motor, transmisión y componentes mecánicos con pocos kilómetros tienen mayor valor de reventa.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-tools-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Estado General</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Evaluamos el estado de la carrocería, motor, transmisión, electrónica, interior y neumáticos. Cada componente en buen estado suma valor al vehículo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-file-damage-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Daños o Averías</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Si el coche tiene daños por accidente, averías mecánicas o problemas de ITV, esto afecta al valor. Sin embargo, muchas piezas pueden seguir siendo valiosas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-shopping-cart-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Demanda de Piezas</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Modelos con alta demanda de recambios en el mercado tienen mejor valoración. Nuestra experiencia nos permite conocer qué piezas son más solicitadas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-file-list-3-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Documentación</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        Vehículos con documentación completa y al día (ITV, seguro, libro de mantenimiento) tienen mejor valoración que aquellos con problemas administrativos.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-scales-line text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332] text-lg">Peso y Materiales</strong>
                      <p className="text-gray-600 leading-relaxed mt-1">
                        El valor del metal, aluminio y otros materiales reciclables también se considera en la tasación final, especialmente en vehículos con muchos daños.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Sección 3 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Tipos de Vehículos que Tasamos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                En <strong>Desguace Barcelona</strong> tasamos y compramos todo tipo de vehículos, sin importar su estado:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center">
                      <i className="ri-car-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2332]">Coches Siniestrados</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    Vehículos declarados siniestro total por la aseguradora. Ofrecemos precios superiores al valor residual estimado por las compañías de seguros.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center">
                      <i className="ri-tools-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2332]">Coches Averiados</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    Vehículos con averías mecánicas, eléctricas o de motor. Evaluamos las piezas en buen estado y te ofrecemos un precio justo.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center">
                      <i className="ri-file-warning-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2332]">Coches Sin ITV</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    Vehículos que no han pasado la ITV o llevan años sin revisión. Te ahorramos el coste y las molestias de intentar pasar la inspección.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center">
                      <i className="ri-time-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2332]">Coches Antiguos</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    Vehículos con muchos años que ya no son rentables de mantener. Muchas piezas clásicas tienen valor para coleccionistas y restauradores.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center">
                      <i className="ri-alert-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2332]">Coches Accidentados</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    Vehículos con daños por colisión que no han sido declarados siniestro total pero que prefieres vender en lugar de reparar.
                  </p>
                </div>

                <div className="bg-[#f8f9fa] p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2332]">Coches en Buen Estado</h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    También compramos vehículos en perfecto estado si necesitas vender rápido. Ofrecemos precios competitivos y proceso inmediato.
                  </p>
                </div>
              </div>
            </section>

            {/* Sección 4 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Ventajas de Nuestra Tasación vs Otras Opciones
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#1a2332] text-white">
                      <th className="p-4 text-left font-bold">Característica</th>
                      <th className="p-4 text-center font-bold">Desguace Barcelona</th>
                      <th className="p-4 text-center font-bold">Venta Particular</th>
                      <th className="p-4 text-center font-bold">Concesionario</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="p-4 font-medium text-gray-700">Tiempo de venta</td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">3-5 días</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Semanas/meses</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">1-2 semanas</p>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-[#f8f9fa]">
                      <td className="p-4 font-medium text-gray-700">Precio ofrecido</td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Muy competitivo</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Potencialmente alto</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Bajo</p>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-4 font-medium text-gray-700">Gestión de trámites</td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Incluida</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Por tu cuenta</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Incluida</p>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-[#f8f9fa]">
                      <td className="p-4 font-medium text-gray-700">Recogida del vehículo</td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Gratis</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Comprador decide</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Debes llevarlo</p>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-4 font-medium text-gray-700">Coches dañados</td>
                      <td className="p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Sí</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">Difícil vender</p>
                      </td>
                      <td className="p-4 text-center">
                        <i className="ri-close-circle-fill text-gray-400 text-2xl"></i>
                        <p className="text-sm text-gray-600 mt-1">No aceptan</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Sección 5 - FAQ */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Preguntas Frecuentes sobre Tasación de Coches
              </h2>

              <div className="space-y-4">
                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿La tasación es realmente gratuita?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Sí, completamente gratuita y sin ningún compromiso. No cobramos nada por evaluar tu vehículo y ofrecerte un precio. Solo pagas si decides aceptar nuestra oferta y vender el coche.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Cuánto tiempo tarda la tasación?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Recibirás una oferta detallada en menos de 24 horas desde que nos proporcionas la información del vehículo. En casos urgentes, podemos acelerar el proceso y darte una respuesta en pocas horas.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Qué información necesitan para tasar mi coche?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Necesitamos: matrícula, marca, modelo, año de fabricación, kilometraje, estado general (funcionando, averiado, accidentado, etc.) y, si es posible, algunas fotos del vehículo. Cuanta más información proporciones, más precisa será la tasación.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Puedo solicitar una tasación si el coche no está en Barcelona?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Nuestra área principal de servicio es Barcelona y provincia, donde ofrecemos recogida gratuita. Si el vehículo está fuera de esta zona, contacta con nosotros y estudiaremos tu caso. En algunos casos podemos ampliar el área de recogida.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿El precio de la tasación es definitivo?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    La oferta que te hacemos es firme siempre que la información proporcionada sea correcta. Si al inspeccionar el vehículo encontramos diferencias significativas con lo descrito, podríamos ajustar el precio. Por eso es importante ser honesto y detallado al describir el estado del coche.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Puedo solicitar una inspección presencial antes de decidir?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Sí, ofrecemos inspección presencial gratuita en Barcelona y alrededores. Un técnico experto se desplazará para evaluar el vehículo in situ y confirmar la oferta final. Este servicio es especialmente útil para coches con daños complejos.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Qué pasa si no acepto la oferta?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    No pasa absolutamente nada. La tasación es sin compromiso. Puedes rechazar la oferta sin dar explicaciones y sin ningún coste. No hay presión ni obligación de vender.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Compran coches de todas las marcas?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Sí, tasamos y compramos vehículos de todas las marcas: Seat, Volkswagen, Renault, Peugeot, Citroën, Ford, Opel, BMW, Mercedes, Audi, Toyota, Nissan, Hyundai, Kia, Fiat, Alfa Romeo y cualquier otra marca del mercado. También compramos furgonetas, todoterrenos y vehículos comerciales.
                  </p>
                </div>
              </div>
            </section>

            {/* Sección 6 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Zonas de Barcelona Donde Ofrecemos Tasación Gratuita
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Servicio de tasación y recogida gratuita en toda Barcelona y provincia:
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Barcelona Ciudad', 'Badalona', 'L\'Hospitalet', 'Sabadell', 'Terrassa', 'Mataró', 'Granollers', 'Vilafranca', 'Sant Cugat', 'Cornellà', 'Rubí', 'Manresa', 'Vilanova', 'Gavà', 'Castelldefels', 'Provincia'].map((zona, index) => (
                  <div key={index} className="bg-[#f8f9fa] p-3 rounded-lg text-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-1"></i>
                    <span className="text-sm font-medium text-[#1a2332]">{zona}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Final */}
            <section className="bg-gradient-to-br from-[#1a2332] to-[#2d3e50] rounded-2xl p-12 text-center text-white">
              <h2 className="text-4xl font-black mb-4">
                Solicita tu Tasación Gratuita Hoy
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Sin compromiso • Respuesta en 24h • Mejor precio garantizado
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20tasar%20mi%20coche%20gratis" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1da851] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-3"></i>
                  Tasar mi Coche Ahora
                </a>
                <a 
                  href="tel:+34932932913" 
                  className="inline-flex items-center justify-center px-10 py-5 bg-white text-[#1a2332] rounded-full font-bold text-lg hover:bg-[#e55a2b] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-3"></i>
                  932 932 913
                </a>
              </div>
            </section>

          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
